import React from "react"

import {User} from "../types/User"
import {UserApi} from "../services/UserApi"
import {MessageInterface} from "../components/common/Message"
import UserLockingSystemPermissionApi from "../services/UserLockingSystemPermissionApi"
import {UserLockingSystemPermissionAdd} from "@customTypes/UserLockingSystemPermission"
import LockingSystemPermission from "@customTypes/LockingSystemPermission"

export type UserContextType = {
    addItem:    (user: User) => Promise<User | void> | void
    deleteItem: (user: User) => Promise<any> | void
    editItem:   (user: User) => Promise<User | void> | void
    fetchItem:  (id: number, include?: string) => void;
    fetchItems: (search?: string, force?: boolean) => void
    items:      User[] | undefined

    addLockingSystemPermissions:       (item: User, lockingSystemPermissionIds: number[]) => Promise<void> | void
    deleteLockingSystemPermission:     (item: User, lockingSystemPermission:    LockingSystemPermission) => Promise<void> | void
    deleteAllLockingSystemPermissions: (item: User) => Promise<void> | void

    inProgress:   boolean
    message:      MessageInterface | undefined
    closeMessage: () => void

    loadingItems: boolean
}

const initialState = {
    addItem:      () => {},
    deleteItem:   () => {},
    editItem:     () => {},
    fetchItem:    () => {},
    fetchItems:   () => {},
    items:        undefined,
    loadingItems: true,

    addLockingSystemPermissions:       () => {},
    deleteLockingSystemPermission:     () => {},
    deleteAllLockingSystemPermissions: () => {},

    inProgress:   false,
    message:      undefined,
    closeMessage: () => {}
}

export const UserContext = React.createContext<UserContextType>(initialState)

interface UserProviderProps {
    children: React.ReactNode
}

const UserProvider: React.FC<UserProviderProps> = ( { children }) => {

    const [items, setItems] = React.useState<User[] | undefined>(initialState.items)
    const [loadingItems, setLoadingItems] = React.useState<boolean>(initialState.loadingItems)
    const [inProgress, setInProgress] = React.useState<boolean>(initialState.inProgress)
    const [message, setMessage] = React.useState<MessageInterface | undefined>(initialState.message)

    const fetchItem = async (id: number, include?: string): Promise<User | void> => {
        setLoadingItems(true)

        const result = await UserApi.get(id, include)
            .catch((e) => setErrorMessage(e))

        setLoadingItems(false)

        return result
    }

    const fetchItems = async (search?: string, force: boolean = false) => {
        if (items === undefined || force) {
            setLoadingItems(true)
            setItems( await UserApi.getAll(search) )
            setLoadingItems(false)
        }
    }

    const addItem = async (newItem: User): Promise<User | void> => {
        closeMessage()
        setInProgress(true)

        const result = await UserApi.add(newItem)
            .catch(e => setErrorMessage(e))

        if (items && result) {
            setInProgress(false)
            setItems([result, ...items])
        }

        return result
    }

    const deleteItem = async (item: User): Promise<any> => {
        closeMessage()
        setInProgress(true)

        const result = await UserApi.delete(item.id)
            .catch((e) => setErrorMessage(e))

        if (items && result !== undefined) {
            const newItems = [...items]
            newItems.splice(newItems.findIndex(i => i.id === item.id), 1)

            setInProgress(false)
            setItems(newItems)
        }
    }

    const editItem = async (item: User): Promise<User | void> => {
        closeMessage()
        setInProgress(true)

        const result = await UserApi.edit(item)
            .catch((e) => setErrorMessage(e))

        if (items && result) {
            const newItems: User[] = items.map(oldItem => {
                return oldItem.id === item.id && result.id ? {...result} : oldItem
            })

            setInProgress(false)
            setItems(newItems)
        }

        return result
    }

    const addLockingSystemPermissions = async (
        item: User, lockingSystemPermissionIds: number[]
    ): Promise<any | void> => {
        closeMessage()
        setInProgress(true)

        const userLockingSystemPermissions: UserLockingSystemPermissionAdd = {
            userId:                     item.id,
            lockingSystemId:            0, //|| item.lockingSystemId || 0,
            lockingSystemPermissionIds: lockingSystemPermissionIds
        }

        const result = await UserLockingSystemPermissionApi.add(userLockingSystemPermissions)
            .catch((e) => setErrorMessage(e))

        if (result) {
            const newItems: User[] | undefined = items?.map(item => {
                if (item.id === userLockingSystemPermissions.userId) item.lockingSystemPermissions = result
                return item
            })

            setItems(newItems)
            setInProgress(false)
        }
    }

    const deleteLockingSystemPermission = async (
        user: User, lockingSystemPermission: LockingSystemPermission
    ): Promise<void> => {
        closeMessage()
        setInProgress(true)

        const result = await UserLockingSystemPermissionApi.delete(user, lockingSystemPermission)
            .catch((e) => setErrorMessage(e))

        if (result && items) {
            const newItems: User[] = items.map(u => {
                if (u.id !== user.id) return u

                const newPermissions = [...u.lockingSystemPermissions || []]
                newPermissions.splice(
                    newPermissions.findIndex(fi => fi.id === lockingSystemPermission.id)
                    , 1
                )

                return {...u, lockingSystemPermissions: newPermissions}
            })

            setItems(newItems)
            setInProgress(false)
        }
    }

    const deleteAllLockingSystemPermissions = async (item: User): Promise<void> => {
        closeMessage()
        setInProgress(true)

        const result = await UserLockingSystemPermissionApi.deleteAll(item, 0)
            .catch((e) => setErrorMessage(e))

        const status: boolean = result.data.status === 'success'

        if (status && items) {
            const newItems: User[] = items.map(i => {
                if (i.id === item.id) i.lockingSystemPermissions = []
                return i
            })

            setItems(newItems)
            setInProgress(false)
        }
    }

    const closeMessage = () => setMessage(undefined)

    const setErrorMessage = (e: any) => {
        setMessage({
            text: e.response.data.message || '?',
            type: 'error'
        })

        setInProgress(false)
        setLoadingItems(false)
    }

    return (
        <UserContext.Provider
            value={{
                addItem,
                deleteItem,
                editItem,
                fetchItem,
                fetchItems,
                items,
                loadingItems,
                inProgress,

                message,
                closeMessage,

                addLockingSystemPermissions,
                deleteLockingSystemPermission,
                deleteAllLockingSystemPermissions
            }}
        >
            {children}
        </UserContext.Provider>
    )
}

export const useUsers = () => {
    return React.useContext(UserContext) as UserContextType
}

export default UserProvider